import { Link } from 'react-router-dom'
import { DownloadIcon, ViewsIcon } from '@/components/icons'
import { SensitiveOverlay } from '@/components/SensitiveImage'
import { sensitiveImageClass, useSensitiveMask } from '@/hooks'
import { ImagePlaceholder, Skeleton } from '@/components/ui'
import type { PostCardModel } from '@/types/ui'
import { cn } from '@/utils/cn'
import { formatCompactCount } from '@/utils/format'

/**
 * 资源卡片 —— 设计稿里出现频率最高的元素（D1 两处网格、D3 结果网格、M1/M3 列表）。
 *
 * 布局要点（对照 design_draft）：
 *   - 上方 16:9 封面，下方标题 / 作者 / 统计三行，整体一个圆角描边卡片；
 *   - 统计行是「眼睛 + 浏览量」「下箭头 + 下载量」两组，靠左排列；
 *   - hover 时卡片轻微抬起（阴影加深），标题转主色。
 *
 * 整卡可点，但**只有标题是链接**：把 `<a>` 套在整张卡上会让读屏器把封面、作者、
 * 统计全部念成链接文本。这里用 `after:absolute inset-0` 让标题链接的点击区域覆盖
 * 整卡 —— 视觉上整卡可点，语义上只有一个链接，而且用户仍能选中卡片里的文字。
 *
 * ## 卡片高度是固定的
 *
 * 封面是 16:9，高度由列宽唯一决定；文字区固定 `BODY_HEIGHT`，标题固定占两行
 * （`TITLE_HEIGHT`，只有一行时留空）。于是**任意两张卡的高度完全相同**，网格里
 * 每一行都一样高，换一批数据也不会变。
 *
 * 之前是让文字区随内容伸缩、靠 grid 的 `items-stretch` 对齐同一行 —— 那样同一行
 * 内是齐的，但行与行之间不齐（第一行有作者、第二行没有，两行就差一截），而且
 * 高度取决于当批数据，翻一页就变。
 *
 * 代价是缺作者 / 缺更新时间的卡会在统计行上方多一块空白（统计行由 `mt-auto`
 * 压在底部）。这是「固定高度」必然的一面：要么留白，要么行高不齐。
 */

/**
 * 文字区高度。改这个值要同时看 `ModCardSkeleton` —— 两处必须一致，
 * 否则骨架屏换成真数据时整个网格会跳一下。
 *
 * 12(上内边距) + 40(标题两行) + 8(标题与作者之间的 `mt-2`) + 16(作者) +
 * 2(作者与统计行之间的 `pt-0.5`) + 16(统计行，更新时间并进这一行) +
 * 12(下内边距) = 106px。
 *
 * 行距不再由容器的 flex `gap` 统一给，而是各行自己写 margin —— 因为
 * 「标题↔作者」要松、「作者↔统计行」要紧，一个 gap 给不出两种间距。
 *
 * 卡片外层是 `overflow-hidden`：这个常量小于上面的账目时，统计行会被直接裁掉
 * 一截，而不是把卡片撑高。
 */
const BODY_HEIGHT = 'h-[6rem]'

/** 标题固定两行：`text-sm` + `leading-5` 即每行 20px。 */
const TITLE_HEIGHT = 'min-h-[1rem]'

export interface ModCardProps {
  post: PostCardModel
  /** 排行序号（设计稿「下载最多」左上角的排行角标）。 */
  rank?: number
  className?: string
}

export function ModCard({ post, rank, className }: ModCardProps) {
  const masked = useSensitiveMask(post.coverUrl, post.coverSensitive)

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card transition-all duration-200 ease-out-soft',
        'hover:-translate-y-0.5 hover:border-border-strong hover:shadow-card-hover',
        'focus-within:border-primary',
        className,
      )}
    >
      <div className="relative aspect-[16/9] shrink-0 overflow-hidden">
        {post.coverUrl ? (
          <>
            <img
              src={post.coverUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className={cn(
                'size-full object-cover transition-transform duration-300 ease-out-soft',
                // 遮罩期间不要那 3% 的 hover 放大：它会盖掉 scale-125，
                // 露出模糊边缘那一圈脏边。
                masked ? sensitiveImageClass : 'group-hover:scale-[1.03]',
              )}
            />
            {masked && <SensitiveOverlay url={post.coverUrl} />}
          </>
        ) : (
          // 后端暂无封面字段，占位图是设计稿定义的常态外观而非异常态。
          // TODO(backend): 关联 docs/backend-sync/2026-08-07-post-card-media-diff.md
          <ImagePlaceholder className="size-full" />
        )}
        {rank !== undefined && (
          <span className="absolute left-2 top-2 flex size-6 items-center justify-center rounded-md bg-navy text-xs font-semibold text-navy-foreground shadow-card">
            {rank}
          </span>
        )}
      </div>

      {/* shrink-0 + 固定高度：网格把整卡拉高时也不许文字区跟着变（见文件头）。 */}
      <div className={cn('flex shrink-0 flex-col p-4', BODY_HEIGHT)}>
        <h3
          className={cn(
            'text-sm font-semibold leading-5 text-foreground transition-colors group-hover:text-primary',
            TITLE_HEIGHT,
          )}
        >
          <Link
            to={`/mods/${post.slug}`}
            // after 伪元素把点击区域撑到整卡，同时保持只有一个链接节点。
            className="line-clamp-2 after:absolute after:inset-0 after:content-['']"
          >
            {post.title}
          </Link>
        </h3>

        {/*
          作者行：未标注作者时整行不渲染，不画一个空占位。
          卡片高度已经固定，少一行不会让这张卡变矮 —— 空出来的位置由下面
          `mt-auto` 的统计行吃掉，也就是白一小块，而不是错位。

          `mt-2` 只加在这一行上：作者要和标题拉开、和下面的统计行贴在一起，读起来
          才是「标题」+「一小块元信息」两组，而不是间距均匀的三行。
        */}
        {post.authorName && (
          <p className="mt-2 truncate text-xs text-muted-foreground">{post.authorName}</p>
        )}

        {/*
          作者手填的「更新时间」（后端 `update_period`，只到月）。
          它是站点五栏之一，读者判断「这东西还新不新」靠的是它而不是首次发布时间。
          未标注时整行不渲染，理由同上面的作者行。
        */}

        <div className="mt-0 flex items-center gap-3 pt-0 text-xs text-muted-foreground">
          <StatItem
            icon={<ViewsIcon className="size-3.5" />}
            value={post.viewCount}
            label="浏览量"
          />
          <StatItem
            icon={<DownloadIcon className="size-3.5" />}
            value={post.downloadCount}
            label="下载量"
          />
          {post.updatedLabel && (
            <p className="truncate text-xs text-subtle-foreground">更新于 {post.updatedLabel}</p>
          )}
        </div>
      </div>
    </article>
  )
}

/**
 * 单项统计。
 *
 * `aria-label` 带上「浏览量 8.0k」这样的完整语义：图标对读屏器不可见，
 * 只念一个「8.0k」等于没说。
 */
function StatItem({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  const formatted = formatCompactCount(value)
  return (
    <span className="inline-flex items-center gap-1" aria-label={`${label} ${formatted}`}>
      <span className="text-subtle-foreground" aria-hidden="true">
        {icon}
      </span>
      <span className="tabular-nums">{formatted}</span>
    </span>
  )
}

/**
 * 移动端的横向列表项（设计稿 M3 搜索结果）。
 *
 * 移动端窄屏放两列卡片会让标题挤成三行，所以搜索结果改成「左缩略图 + 右文字」
 * 的行式布局 —— 这是 AGENTS.md 4.3 要求的「分别实现」，不是把桌面网格缩小。
 */
export function ModCardRow({ post, className }: { post: PostCardModel; className?: string }) {
  return (
    <article
      className={cn(
        'group relative flex items-center gap-3 rounded-card border border-border bg-surface p-2.5 shadow-card transition-colors',
        'focus-within:border-primary hover:border-border-strong',
        className,
      )}
    >
      <div className="size-16 shrink-0 overflow-hidden rounded-lg">
        {post.coverUrl ? (
          <img
            src={post.coverUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <ImagePlaceholder className="size-full" iconClassName="size-6" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-foreground group-hover:text-primary">
          <Link
            to={`/mods/${post.slug}`}
            className="after:absolute after:inset-0 after:content-['']"
          >
            {post.title}
          </Link>
        </h3>
        {post.authorName && (
          <p className="truncate text-xs text-muted-foreground">{post.authorName}</p>
        )}
        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <StatItem
            icon={<ViewsIcon className="size-3.5" />}
            value={post.viewCount}
            label="浏览量"
          />
          <StatItem
            icon={<DownloadIcon className="size-3.5" />}
            value={post.downloadCount}
            label="下载量"
          />
        </div>
      </div>
    </article>
  )
}

/**
 * 卡片骨架屏。
 *
 * 尺寸与 ModCard 逐项对齐（同样的 16:9、同样的三行文字高度），这样加载完成时
 * 不会有布局跳动 —— 骨架屏的意义就在于此，尺寸不对反而比转圈更糟。
 */
export function ModCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-card border border-border bg-surface',
        className,
      )}
    >
      <Skeleton className="aspect-[16/9] rounded-none" />
      {/* 与 ModCard 用同一个高度常量：不一致的话真数据回来时整个网格会跳一下。 */}
      <div className={cn('flex flex-col p-3', BODY_HEIGHT)}>
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="mt-2 h-3 w-2/5" />
        <div className="mt-auto flex gap-3 pt-0.5">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-12" />
        </div>
      </div>
    </div>
  )
}

/** 网格骨架屏：`count` 与真实每页条数一致，避免加载后行数突变。 */
export function ModCardGridSkeleton({ count = 10 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <ModCardSkeleton key={index} />
      ))}
    </>
  )
}
